import { Box } from '@mui/material'
import {
  GridColDef,
  GridFooterContainer,
  GridPagination,
  GridRowClassNameParams,
  useGridApiRef,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid-premium'
import DataGrid from 'components/DataGrid/DataGrid'
import COLORS from 'lib/colors'
import { useOrbit } from 'providers/OrbitProvider'
import { failOnHttpError } from 'lib/utils'
import { useNotifications } from 'lib/NotificationsProvider'
import LeadDetailsDrawer from '../components/LeadDetailsDrawer'
import { buildLeadsRequestBody, getStatusFilterValues, underscoredToDashed } from '../leadTableQuery'
import useSingleFlightRequest from 'components/DataGrid/useSingleFlightRequest'
import AiChatContainer from '../ai/AiChatContainer'
import { LeadRow, LeadTableProps } from './leadTableTypes'
import { mapLeadToRow } from './leadTableMappers'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { buildLeadTableColumns } from './buildLeadTableColumns'
import { buildLeadTableColumnsWithFilters } from '../filters/buildLeadTableColumnsWithFilters'
import { applyColumnOrder, savePageSize } from '../leadTablePreferences'


const PAGE_SIZE_OPTIONS = [25, 50, 100]
const DEFAULT_TIMEZONE = 'America/New_York'

const DATA_GRID_INITIAL_STATE = {
  pinnedColumns: { right: ['rowMenu'] },
}

const HIDDEN_FROM_COLUMN_SELECTOR_FIELDS = ['rowMenu']

const DATA_GRID_SX = {
  border: `1px solid ${COLORS.mediumGray}`,
  borderBottom: 'none',
  boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
  borderRadius: '10px',
  ['.MuiDataGrid-columnHeader, .MuiDataGrid-cell']: {
    pl: 2,
  },
  '& .even': {
    backgroundColor: 'white',
  },
  '& .odd': {
    backgroundColor: COLORS.backgroundGray,
  },
  '& .even .MuiDataGrid-cell.menu-col': {
    backgroundColor: 'white',
  },
  '& .odd .MuiDataGrid-cell.menu-col': {
    backgroundColor: COLORS.backgroundGray,
  },
  '& .MuiDataGrid-columnHeader.menu-col, & .MuiDataGrid-cell.menu-col': {
    paddingLeft: '10px',
    paddingRight: '10px',
  },
}

const DATA_GRID_SLOTS = {
  footer: LeadTableFooter,
}

function getLeadRowClassName(params: GridRowClassNameParams<LeadRow>) {
  return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
}

function getTogglableColumns(columns: GridColDef[]) {
  return columns
    .filter((column) => !HIDDEN_FROM_COLUMN_SELECTOR_FIELDS.includes(column.field))
    .map((column) => column.field)
}

export type LeadApiRecord = {
  id: string
  type: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>
  relationships?: Record<string, unknown>
}

type LeadsApiResponse = {
  data: LeadApiRecord[]
  meta?: {
    total?: number
  }
}

type LeadTableFooterProps = {
  permitAiAnalysis: boolean
  totalCount: number
  loading: boolean
  requestBodyJson: string
}

function LeadTableFooter({ permitAiAnalysis, totalCount, loading, requestBodyJson }: LeadTableFooterProps) {


  return (
    <GridFooterContainer>
      <Box sx={{ p: 1 }}>
        {permitAiAnalysis &&
          <AiChatContainer
            totalCount={totalCount}
            loading={loading}
            requestBodyJson={requestBodyJson}
          />
        }
      </Box>
      <Box sx={{ ml: 'auto' }}>
        <GridPagination />
      </Box>
    </GridFooterContainer>
  )
}

export function LeadsDataGrid({
  timezone = DEFAULT_TIMEZONE,
  refreshKey = 0,
  refreshTable,
  setLeadMeta,
  onLoadingChange,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  columnOrder,
  onColumnOrderChange,
  externalFilters,
  onExternalFiltersChange,
  clientIdToName,
  clientCampaignIdToName,
  vendorIdToName,
  degreeProgramIdToName,
  degreeProgramsByClient,
  statuses,
  dispositions,
  clientCampaignCustomAttributes,
  permitLandingPages,
  permitDelete,
  permitAiAnalysis,
  userHasMultipleClients,
  userHasMultipleVendors,
}: LeadTableProps) {
  const [showLeadDetails, setShowLeadDetails] = useState<LeadRow | null>()

  const requestLeads = useCallback(
    async ({ requestBodyJson }: { requestBodyJson: string }) => {
      const response = await fetch('/api/v1/leads', {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
        },
        body: requestBodyJson,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch leads: ${response.status}`)
      }

      return response.json() as Promise<LeadsApiResponse>
    },
    []
  )

  const {
    data: response,
    error,
    loading,
    run,
  } = useSingleFlightRequest<{ requestBodyJson: string }, LeadsApiResponse>({
    request: requestLeads,
  })

  const { remote } = useOrbit()
  const { addNotification } = useNotifications()
  const apiRef = useGridApiRef()

  const requestBody = useMemo(() => {
    return buildLeadsRequestBody({
      paginationModel,
      sortModel,
      filterModel,
      externalFilters,
      timezone,
    })
  }, [paginationModel, sortModel, filterModel, externalFilters, timezone])

  const requestBodyJson = useMemo(() => {
    return JSON.stringify(requestBody)
  }, [requestBody])

  const requestKey = useMemo(() => {
    return `${refreshKey}:${requestBodyJson}`
  }, [refreshKey, requestBodyJson])

  const handleLeadDetails = useCallback((row: LeadRow) => {
    setShowLeadDetails(row)
  }, [])

  const handleHideDetails = useCallback(() => {
    setShowLeadDetails(null)
  }, [])

  const handleDeleteLead = useCallback((row: LeadRow) => {
    const leadName = [row.firstName, row.lastName].filter(Boolean).join(' ').trim()
    const isMaskedLeadName = leadName !== '' && /^[*\s]+$/.test(leadName)
    const leadLabel = !leadName || isMaskedLeadName ? `with ID: ${row.id}` : `record for ${leadName}`

    if (!confirm(`Are you sure you want to delete the lead ${leadLabel}?`)) {
      return
    }

    fetch(remote.requestProcessor.urlBuilder.resourceURL('lead', row.id), {
      method: 'DELETE',
    })
      .then(failOnHttpError)
      .then(() => {
        addNotification({ variant: 'notice', message: 'Deleted lead.' })
        refreshTable()
      })
      .catch(() => {
        addNotification({ variant: 'alert', message: 'Failed to delete lead.' })
      })
  }, [addNotification, refreshTable, remote.requestProcessor.urlBuilder])

  useEffect(() => {
    run({ requestBodyJson })
  }, [run, requestKey, requestBodyJson])

  useEffect(() => {
    onLoadingChange?.(loading)
  }, [loading, onLoadingChange])

  useEffect(() => {
    if (loading) return

    const meta = response?.meta
    if (!meta) return

    const selectedStatuses = getStatusFilterValues(externalFilters)
    const selectedLeadStatuses = selectedStatuses.map(underscoredToDashed)
    const counts = meta['counts-by-status'] ?? {}

    const backFilledCounts = selectedLeadStatuses.reduce<Record<string, number>>(
      (acc, status) => {
        if (!(status in acc)) {
          acc[status] = 0
        }

        return acc
      },
      { ...counts }
    )

    setLeadMeta({
      ...meta,
      'counts-by-status': backFilledCounts,
    })

    // Intentionally excluding externalFilters from deps.
    // Backfill must use the filters associated with the completed response,
    // not newer filters that may have changed before loading flips true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, loading, setLeadMeta])

  const rows = useMemo(() => {
    return response?.data?.map(mapLeadToRow) ?? []
  }, [response])

  const rowCount = response?.meta?.total ?? 0

  const columns = useMemo(() => {
    const unorderedColumns = buildLeadTableColumns({
      timezone,
      clientIdToName,
      clientCampaignIdToName,
      vendorIdToName,
      degreeProgramIdToName,
      statuses,
      dispositions,
      permitLandingPages,
      userHasMultipleClients,
      userHasMultipleVendors,
      permitDelete,
      onLeadDetails: handleLeadDetails,
      onDeleteLead: handleDeleteLead,
      clientCampaignCustomAttributes,
    })

    return applyColumnOrder(unorderedColumns, columnOrder, columnVisibilityModel)
  }, [
    timezone,
    clientIdToName,
    clientCampaignIdToName,
    vendorIdToName,
    degreeProgramIdToName,
    statuses,
    dispositions,
    permitLandingPages,
    userHasMultipleClients,
    userHasMultipleVendors,
    permitDelete,
    handleLeadDetails,
    handleDeleteLead,
    clientCampaignCustomAttributes,
    columnOrder,
    columnVisibilityModel,
  ])

  const handlePaginationModelChange = useCallback((nextModel: GridPaginationModel) => {
    if (nextModel.pageSize !== paginationModel.pageSize) {
      savePageSize(nextModel.pageSize)
    }

    onPaginationModelChange(nextModel)
  }, [onPaginationModelChange, paginationModel.pageSize])

  const handleSortModelChange = useCallback((nextModel: GridSortModel) => {
    onSortModelChange(nextModel)
    onPaginationModelChange((prev) => ({ ...prev, page: 0 }))
  }, [onPaginationModelChange, onSortModelChange])

  const handleFilterModelChange = useCallback((nextModel: GridFilterModel) => {
    onFilterModelChange(nextModel)
    onPaginationModelChange((prev) => ({ ...prev, page: 0 }))
  }, [onFilterModelChange, onPaginationModelChange])

  const handleColumnOrderChange = useCallback(() => {
    const api = apiRef.current
    if (!api) return

    onColumnOrderChange(api.getAllColumns().map((column) => column.field))
  }, [apiRef, onColumnOrderChange])

  const columnsWithFilters = useMemo(() => {
    return buildLeadTableColumnsWithFilters({
      columns,
      externalFilters,
      timezone,
      clientIdToName,
      vendorIdToName,
      degreeProgramIdToName,
      degreeProgramsByClient,
      statuses,
      dispositions,
      onExternalFiltersChange,
      onPaginationModelChange,
    })
  }, [
    columns,
    externalFilters,
    timezone,
    clientIdToName,
    vendorIdToName,
    degreeProgramIdToName,
    degreeProgramsByClient,
    statuses,
    dispositions,
    onExternalFiltersChange,
    onPaginationModelChange,
  ])

  const slotProps = useMemo(
    () => ({
      footer: {
        permitAiAnalysis,
        totalCount: rowCount,
        loading,
        requestBodyJson,
      },
      columnsManagement: {
        getTogglableColumns,
      },
    }),
    [permitAiAnalysis, rowCount, loading, requestBodyJson]
  )

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columnsWithFilters}
        apiRef={apiRef}
        onColumnOrderChange={handleColumnOrderChange}
        rowCount={rowCount}
        loading={loading}
        paginationMode='server'
        sortingMode='server'
        filterMode='server'
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        columnHeaderHeight={56}
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        filterDebounceMs={400}
        slots={DATA_GRID_SLOTS}
        slotProps={slotProps}
        initialState={DATA_GRID_INITIAL_STATE}
        getRowClassName={getLeadRowClassName}
        sx={DATA_GRID_SX}
      />

      {error ? (
        <div style={{ marginTop: 8, color: 'red' }}>
          {error}
        </div>
      ) : null}

      <LeadDetailsDrawer columns={columns} lead={showLeadDetails} onClose={handleHideDetails} />
    </div>
  )
}
